// ADR 1.10.26p §2 — one fact: whether React has committed the first time.
//
// On this SPA window `load` fires *before* the first paint, because the render
// awaits `initI18n`. So "after `load`" alone could start the unhinted wasm
// warm-up (or Google's script, §3) before the observed LCP, and Lighthouse's
// lantern charges every request that starts before it to the simulated LCP.
// Both now wait for this signal as well.
//
// `StaticShellCommit`'s layout effect marks it. That effect runs before the
// browser paints the commit, and so does the `requestAnimationFrame` scheduled
// from it. ADR 3.10.26e §3: an idle callback straight after that one frame can
// still run in the commit frame's own idle period — produced, not yet
// presented — so the gated requests started 14–30 ms before observed LCP. A
// rAF nested inside the first runs at the start of the *next* frame, after the
// commit's frame has been produced, and the idle (or `setTimeout`) stage comes
// after that. Neither frame nor the idle stage may be "optimised" away. If two
// frames prove not enough, the answer is a `paint` PerformanceObserver, never a
// third frame. On iOS Safari, which has no `requestIdleCallback`, the
// `setTimeout` fallback is what runs.
//
// Module state, idempotent — StrictMode runs a layout effect twice in
// development, and only the first mark flushes (the `layout/analytics.ts`
// pattern). Imports nothing, so it is a boot-graph leaf (AC8).

/** The window seams `afterFirstPaint` calls — always as methods on `win`, never destructured. */
export type PaintWindow = Pick<Window, 'requestAnimationFrame' | 'setTimeout'> &
  Partial<Pick<Window, 'requestIdleCallback'>>

/** The idle wait's ceiling, so a busy page cannot starve what waits on it (25.9.26q §3's number). */
const IDLE_TIMEOUT_MS = 2000

let committed = false
let waiting: Array<() => void> = []

/** Called from StaticShellCommit's layout effect. Idempotent: only the first call flushes. */
export function markFirstCommit(): void {
  if (committed) return
  committed = true
  const queued = waiting
  waiting = []
  queued.forEach((run) => run())
}

/**
 * Run `fn` once, after the first commit has painted: two nested requestAnimationFrames, then
 * requestIdleCallback({ timeout: 2000 }), or setTimeout(0) where there is none (Safari).
 */
export function afterFirstPaint(win: PaintWindow, fn: () => void): void {
  const idle = () => {
    if (win.requestIdleCallback) win.requestIdleCallback(() => fn(), { timeout: IDLE_TIMEOUT_MS })
    else win.setTimeout(fn, 0)
  }
  const run = () => {
    win.requestAnimationFrame(() => win.requestAnimationFrame(idle))
  }
  if (committed) run()
  else waiting.push(run)
}

export function resetFirstPaintForTests(): void {
  committed = false
  waiting = []
}
